// Centralized mock data. Transcribed from the design prototypes so the mocked
// app shows the same content the designs intended. The future backend returns
// these same shapes (see src/types and FRONTEND_SPEC.md).

import type {
  ActiveGeneration,
  ActivityItem,
  Channel,
  ChannelSummary,
  Command,
  CreditSegment,
  DashboardStats,
  GenerateConfig,
  LibraryItem,
  Plan,
  ProviderRow,
  RenderJob,
  TeamMember,
  User,
  WebhookRow,
} from "../types";

export const CURRENT_USER: User = {
  id: "usr_eli",
  name: "Eli Vance",
  initials: "EV",
  email: "eli@vocaler.studio",
  role: "owner",
};

// Provider catalog — the mock stand-in for GET /web/integrations. In production
// this data comes from the BFF; here it lives in the fixtures (mock) layer only,
// so no provider metadata is hardcoded in components or services logic.
export const PROVIDERS: ProviderRow[] = [
  {
    id: "suno", label: "Suno", kind: "music", configured: true, enabled: true, modelCount: 2,
    envVar: "REELFORGE_SUNO_API_KEY", envVarAliases: ["SUNO_API_KEY"], docsUrl: "https://suno.com/docs",
    creditsAvailableUsd: 12.4, creditsUsedPeriodUsd: 4.2, lastUsedAt: "2026-05-31T07:00:00Z",
    lastTestAt: "2026-05-31T06:30:00Z", last4: "x9d2", lastTestOk: true,
    defaultModels: [
      { id: "suno-v5", label: "Suno v5", kind: "music", creditsPerGeneration: 2 },
      { id: "suno-v4", label: "Suno v4", kind: "music", creditsPerGeneration: 1 },
    ],
    extraFields: [],
    secondaryEnvVar: "",
    primaryLabel: "",
    secondaryLabel: "",
    secondaryConfigured: false,
  },
  {
    id: "lyria", label: "Lyria", kind: "music", configured: true, enabled: true, modelCount: 1,
    envVar: "REELFORGE_LYRIA_API_KEY", envVarAliases: ["LYRIA_API_KEY", "GOOGLE_LYRIA_KEY"], docsUrl: "https://deepmind.google/lyria",
    creditsAvailableUsd: null, creditsUsedPeriodUsd: 1.1, lastUsedAt: "2026-05-30T18:00:00Z",
    lastTestAt: "2026-05-30T17:00:00Z", last4: "k8m1", lastTestOk: true,
    defaultModels: [{ id: "lyria-2", label: "Lyria 2", kind: "music", creditsPerGeneration: 2 }],
    extraFields: [],
    secondaryEnvVar: "",
    primaryLabel: "",
    secondaryLabel: "",
    secondaryConfigured: false,
  },
  {
    id: "firefly", label: "Firefly", kind: "image", configured: true, enabled: true, modelCount: 2,
    envVar: "REELFORGE_FIREFLY_API_KEY", envVarAliases: ["ADOBE_FIREFLY_KEY"], docsUrl: "https://firefly.adobe.com",
    creditsAvailableUsd: 40, creditsUsedPeriodUsd: 0.8, lastUsedAt: "2026-05-29T12:00:00Z",
    lastTestAt: "2026-05-29T11:00:00Z", last4: "fa4c", lastTestOk: true,
    defaultModels: [{ id: "firefly-image-3", label: "Firefly Image 3", kind: "image", creditsPerGeneration: 1 }],
    extraFields: [],
    secondaryEnvVar: "",
    primaryLabel: "",
    secondaryLabel: "",
    secondaryConfigured: false,
  },
  {
    id: "seedance", label: "Seedance", kind: "video", configured: true, enabled: false, modelCount: 1,
    envVar: "REELFORGE_SEEDANCE_API_KEY", envVarAliases: ["SEEDANCE_API_KEY"], docsUrl: "https://seedance.ai/docs",
    creditsAvailableUsd: 5.5, creditsUsedPeriodUsd: 9.3, lastUsedAt: "2026-05-25T09:00:00Z",
    lastTestAt: "2026-05-25T08:00:00Z", last4: "s7e3", lastTestOk: false,
    defaultModels: [{ id: "seedance-2", label: "Seedance 2", kind: "video", creditsPerGeneration: 6 }],
    extraFields: [],
    secondaryEnvVar: "",
    primaryLabel: "",
    secondaryLabel: "",
    secondaryConfigured: false,
  },
  {
    id: "veo3", label: "Veo 3", kind: "video", configured: false, enabled: false, modelCount: 0,
    envVar: "REELFORGE_VEO3_API_KEY", envVarAliases: ["VEO3_API_KEY", "GOOGLE_VEO_KEY"], docsUrl: "https://deepmind.google/veo",
    creditsAvailableUsd: null, creditsUsedPeriodUsd: 0, lastUsedAt: null,
    lastTestAt: null, last4: null, lastTestOk: null, defaultModels: [], extraFields: [], secondaryEnvVar: "", primaryLabel: "", secondaryLabel: "", secondaryConfigured: false,
  },
  {
    id: "s3", label: "Amazon S3", kind: "storage", configured: false, enabled: false, modelCount: 0,
    envVar: "REELFORGE_S3_ACCESS_KEY", envVarAliases: ["AWS_ACCESS_KEY_ID"], docsUrl: "https://docs.aws.amazon.com/s3",
    creditsAvailableUsd: null, creditsUsedPeriodUsd: 0, lastUsedAt: null,
    lastTestAt: null, last4: null, lastTestOk: null, defaultModels: [], extraFields: [], secondaryEnvVar: "", primaryLabel: "", secondaryLabel: "", secondaryConfigured: false,
  },
];

// Mock stand-in for GET /web/team.
export const TEAM_MEMBERS: TeamMember[] = [
  { userId: "usr_eli", email: "eli@vocaler.studio", name: "Eli Vance", role: "owner", status: "active", emailVerifiedAt: "2026-04-01T00:00:00Z", lastSeenAt: "2026-05-31T08:00:00Z", isSelf: true },
  { userId: "usr_mara", email: "mara@vocaler.studio", name: "Mara Quinn", role: "admin", status: "active", emailVerifiedAt: "2026-04-03T00:00:00Z", lastSeenAt: "2026-05-30T20:00:00Z", isSelf: false },
  { userId: "usr_dom", email: "dom@drift.records", name: "Dom Reyes", role: "member", status: "active", emailVerifiedAt: "2026-04-10T00:00:00Z", lastSeenAt: "2026-05-28T14:00:00Z", isSelf: false },
  { userId: "usr_ivy", email: "ivy@drift.records", name: null, role: "member", status: "invited", emailVerifiedAt: null, lastSeenAt: null, isSelf: false },
  { userId: "usr_sol", email: "sol@vocaler.studio", name: "Sol Tan", role: "member", status: "suspended", emailVerifiedAt: "2026-04-12T00:00:00Z", lastSeenAt: "2026-05-10T09:00:00Z", isSelf: false },
];

export const CURRENT_PLAN: Plan = {
  name: "Music + Video Light",
  renews: "jun 12",
  creditsTotal: 2000,
  creditsUsed: 753,
};

export const DASHBOARD_STATS: DashboardStats = {
  credits: 1247,
  creditsPercent: 62,
  generations: 127,
  generationsDelta: 18,
  inQueue: 4,
  queueRendering: 2,
  queueQueued: 2,
  published: 23,
  publishedChannels: 3,
};

export const ACTIVE_GENERATIONS: ActiveGeneration[] = [
  {
    state: "rendering",
    title: "Summer Pulse",
    type: "music + video",
    model: "Suno v5",
    progress: 78,
    time: "00:03:48",
  },
  {
    state: "rendering",
    title: "Drive North",
    type: "music",
    model: "Lyria",
    progress: 31,
    time: "00:01:12",
  },
  {
    state: "queued",
    title: "Late Channel",
    type: "video",
    model: "Seedance",
    progress: 0,
    time: "—",
  },
  {
    state: "complete",
    title: "Hold the Line",
    type: "music",
    model: "Suno v5",
    progress: 100,
    time: "00:04:22",
  },
];

export const CREDIT_BREAKDOWN: CreditSegment[] = [
  { label: "Music", value: 62, color: "#6B8AFF" },
  { label: "Image", value: 22, color: "#FF2D1F" },
  { label: "Video", value: 16, color: "#22C55E" },
];

export const CHANNEL_SUMMARIES: ChannelSummary[] = [
  { platform: "TikTok", handle: "@vocaler_demo", state: "complete", mark: "T", bg: "#0A0A0B" },
  { platform: "YouTube", handle: "@vocaler_studio", state: "complete", mark: "▶", bg: "#FF2D1F" },
  {
    platform: "Instagram",
    handle: "@drift_records",
    state: "rendering",
    mark: "◎",
    bg: "linear-gradient(135deg,#833AB4,#FD1D1D,#FCAF45)",
  },
];

export const ACTIVITY: ActivityItem[] = [
  { state: "complete", text: 'Published "Summer Pulse" to @vocaler_demo', ago: "2m" },
  { state: "rendering", text: "Drive North · video rendering", ago: "8m" },
  { state: "complete", text: "Hold the Line · music complete", ago: "14m" },
  { state: "amber", text: "Credits below 20% — auto top-up off", ago: "1h" },
  { state: "complete", text: "Connected channel @drift_records", ago: "3h" },
];

export const RECENT_WORK = [
  {
    title: "Half Light",
    kind: "music" as const,
    meta: "music · 3:00 · Suno v5",
    palette: "linear-gradient(135deg,#10131d,#17203b,#26324d)",
    signature: "#6B8AFF",
    status: "complete" as const,
  },
  {
    title: "Summer Pulse",
    kind: "video" as const,
    meta: "video · 16:9 · 2:47 · Visualizer",
    palette: "linear-gradient(135deg,#0f172a,#0b6b75,#9a6716)",
    status: "rendering" as const,
  },
  {
    title: "Open Frame",
    kind: "image" as const,
    meta: "image · 16:9 · GPT Image",
    palette: "linear-gradient(135deg,#0e3028,#1f5c45,#65875d)",
    status: "complete" as const,
  },
  {
    title: "Half Light",
    kind: "project" as const,
    meta: "project · 3 items · 9:16",
    palette: "linear-gradient(180deg,#201a18,#3a2622,#7a3d2f)",
    status: "complete" as const,
    count: 3,
  },
  {
    title: "May Mix",
    kind: "bundle" as const,
    meta: "bundle · 5 videos · 15:02 · 16:9",
    palette: "linear-gradient(135deg,#121820,#27313a,#55616a)",
    status: "complete" as const,
    count: 5,
  },
];

const pub = {
  published2: {
    status: "published" as const,
    label: "Published to 2 channels",
    destinations: [
      { icon: "▸", platform: "YouTube", handle: "@vocaler_studio", time: "2h ago", ok: true },
      { icon: "▸", platform: "TikTok", handle: "@vocaler_demo", time: "2h ago", ok: true },
    ],
  },
  notPublished: (time: string) => ({
    status: "not_published" as const,
    label: "Not published",
    destinations: [{ icon: "", platform: "Created", handle: "", time }],
  }),
  scheduled: {
    status: "scheduled" as const,
    label: "Scheduled",
    destinations: [
      { icon: "◐", platform: "YouTube", handle: "@vocaler_studio", time: "May 25, 14:30" },
    ],
  },
  partial: {
    status: "partial" as const,
    label: "Partial publish",
    destinations: [
      { icon: "▸", platform: "YouTube", handle: "@vocaler_studio", time: "1d ago", ok: true },
      { icon: "✕", platform: "TikTok", handle: "@vocaler_demo", time: "failed", failed: true },
    ],
  },
  published1ig: {
    status: "published" as const,
    label: "Published to 1 channel",
    destinations: [
      { icon: "▸", platform: "Instagram", handle: "@drift_records", time: "3d ago", ok: true },
    ],
  },
};

export const LIBRARY_COMPONENTS: LibraryItem[] = [
  {
    id: "lib_half_light_music",
    projectId: "lib_half_light_project",
    title: "Half Light",
    kind: "music",
    type: "music",
    aspect: "audio",
    duration: "3:00",
    size: "7.2 MB",
    model: "Suno v5",
    status: "complete",
    created: "2h ago",
    source: "generated",
    format: ".wav",
    signature: "#6B8AFF",
    publish: pub.published2,
    palette: "linear-gradient(135deg,#10131d,#17203b,#26324d)",
  },
  {
    id: "lib_half_light_image",
    projectId: "lib_half_light_project",
    title: "Half Light",
    kind: "image",
    type: "image",
    aspect: "9:16",
    duration: "",
    size: "1.4 MB",
    imageModel: "Nano Banana Pro",
    model: "Nano Banana Pro",
    status: "complete",
    created: "2h ago",
    format: ".png",
    publish: pub.notPublished("2h ago"),
    palette: "linear-gradient(180deg,#2a1f1f,#5b3328,#ba6b3f)",
  },
  {
    id: "lib_half_light_video",
    projectId: "lib_half_light_project",
    title: "Half Light",
    kind: "video",
    type: "video",
    aspect: "9:16",
    duration: "3:00",
    size: "3.8 MB",
    videoModel: "Seedance 2",
    model: "Seedance 2",
    status: "complete",
    created: "2h ago",
    format: ".mp4",
    publish: pub.published2,
    palette: "linear-gradient(180deg,#201a18,#3a2622,#7a3d2f)",
  },
  {
    id: "lib_summer_music",
    projectId: "lib_summer_project",
    title: "Summer Pulse",
    kind: "music",
    type: "music",
    aspect: "audio",
    duration: "2:47",
    size: "6.9 MB",
    model: "Suno v5",
    status: "complete",
    created: "5h ago",
    source: "generated",
    format: ".wav",
    signature: "#22C55E",
    publish: pub.notPublished("5h ago"),
    palette: "linear-gradient(135deg,#101816,#153126,#254238)",
  },
  {
    id: "lib_summer_image",
    projectId: "lib_summer_project",
    title: "Summer Pulse",
    kind: "image",
    type: "image",
    aspect: "16:9",
    duration: "",
    size: "1.8 MB",
    imageModel: "GPT Image",
    model: "GPT Image",
    status: "complete",
    created: "5h ago",
    format: ".png",
    publish: pub.notPublished("5h ago"),
    palette: "linear-gradient(135deg,#10202a,#0f5d6b,#a86f1d)",
  },
  {
    id: "lib_summer_video",
    projectId: "lib_summer_project",
    title: "Summer Pulse",
    kind: "video",
    type: "video",
    aspect: "16:9",
    duration: "2:47",
    size: "10.8 MB",
    videoModel: "Visualizer",
    model: "Visualizer",
    status: "complete",
    created: "5h ago",
    format: ".mp4",
    publish: pub.scheduled,
    palette: "linear-gradient(135deg,#0f172a,#0b6b75,#9a6716)",
  },
  {
    id: "lib_drive_music",
    title: "Drive North",
    kind: "music",
    type: "music",
    aspect: "audio",
    duration: "3:22",
    size: "7.2 MB",
    model: "Lyria 2",
    status: "complete",
    created: "5h ago",
    source: "generated",
    format: ".wav",
    signature: "#F59E0B",
    publish: pub.notPublished("5h ago"),
    palette: "linear-gradient(135deg,#221716,#42201c,#5d2c25)",
  },
  {
    id: "lib_drive_video",
    title: "Drive North",
    kind: "video",
    type: "video",
    aspect: "16:9",
    duration: "2:47",
    size: "11.2 MB",
    videoModel: "Seedance 2",
    model: "Seedance 2",
    status: "complete",
    created: "4h ago",
    format: ".mp4",
    publish: pub.notPublished("4h ago"),
    palette: "linear-gradient(135deg,#18202a,#384453,#647181)",
  },
  {
    id: "lib_drift_video",
    title: "Drift West",
    kind: "video",
    type: "video",
    aspect: "16:9",
    duration: "3:08",
    size: "13.1 MB",
    videoModel: "Seedance 2",
    model: "Seedance 2",
    status: "complete",
    created: "6h ago",
    format: ".mp4",
    publish: pub.notPublished("6h ago"),
    palette: "linear-gradient(135deg,#171c19,#334238,#657163)",
  },
  {
    id: "lib_late_video",
    title: "Late Channel",
    kind: "video",
    type: "video",
    aspect: "9:16",
    duration: "1:00",
    size: "5.4 MB",
    videoModel: "Seedance 2",
    model: "Seedance 2",
    status: "complete",
    created: "1d ago",
    format: ".mp4",
    publish: pub.partial,
    palette: "linear-gradient(135deg,#111827,#283446,#3f4b5f)",
  },
  {
    id: "lib_open_image",
    title: "Open Frame",
    kind: "image",
    type: "image",
    aspect: "16:9",
    duration: "",
    size: "1.4 MB",
    imageModel: "SDXL",
    model: "SDXL",
    status: "complete",
    created: "1d ago",
    format: ".png",
    publish: pub.notPublished("1d ago"),
    palette: "linear-gradient(135deg,#0e3028,#1f5c45,#65875d)",
  },
  {
    id: "lib_hold_video",
    title: "Hold the Line",
    kind: "video",
    type: "video",
    aspect: "9:16",
    duration: "3:00",
    size: "12.4 MB",
    videoModel: "Visualizer",
    model: "Visualizer",
    status: "complete",
    created: "3d ago",
    format: ".mp4",
    publish: pub.published1ig,
    palette: "linear-gradient(135deg,#1c1917,#413d38,#7f6c27)",
  },
];

export const LIBRARY_ITEMS: LibraryItem[] = [
  ...LIBRARY_COMPONENTS.slice(0, 3),
  {
    id: "lib_half_light_project",
    title: "Half Light",
    kind: "project",
    type: "project",
    aspect: "9:16",
    duration: "3:00",
    size: "12.4 MB",
    model: "Suno v5",
    imageModel: "Nano Banana Pro",
    videoModel: "Seedance 2",
    status: "complete",
    created: "2h ago",
    componentIds: ["lib_half_light_music", "lib_half_light_image", "lib_half_light_video"],
    publish: pub.published2,
    palette: "linear-gradient(180deg,#201a18,#3a2622,#7a3d2f)",
  },
  ...LIBRARY_COMPONENTS.slice(3, 6),
  {
    id: "lib_summer_project",
    title: "Summer Pulse",
    kind: "project",
    type: "project",
    aspect: "16:9",
    duration: "2:47",
    size: "19.5 MB",
    model: "Suno v5",
    imageModel: "GPT Image",
    videoModel: "Visualizer",
    status: "complete",
    created: "5h ago",
    componentIds: ["lib_summer_music", "lib_summer_image", "lib_summer_video"],
    publish: pub.scheduled,
    palette: "linear-gradient(135deg,#0f172a,#0b6b75,#9a6716)",
  },
  ...LIBRARY_COMPONENTS.slice(6),
  {
    id: "lib_may_mix_bundle",
    title: "May Mix",
    kind: "bundle",
    type: "bundle",
    aspect: "16:9",
    duration: "15:02",
    size: "88.4 MB",
    videoCount: 5,
    status: "complete",
    created: "1d ago",
    sourceVideoIds: ["lib_summer_video", "lib_late_video", "lib_hold_video"],
    publish: pub.notPublished("1d ago"),
    palette: "linear-gradient(135deg,#121820,#27313a,#55616a)",
  },
];

export const RENDER_JOBS: RenderJob[] = [
  {
    id: "gen_4f8d2c",
    state: "rendering",
    title: "Summer Pulse — 9:16 visualizer",
    type: "music + video",
    model: "Suno v5",
    progress: 78,
    elapsed: "00:03:48",
    cost: 8,
    tab: "active",
  },
  {
    id: "gen_a01b9e",
    state: "rendering",
    title: "Drive North",
    type: "music",
    model: "Lyria",
    progress: 31,
    elapsed: "00:01:12",
    cost: 14,
    tab: "active",
  },
  {
    id: "gen_c2f7d1",
    state: "queued",
    title: "Late Channel · publish",
    type: "video",
    model: "Seedance 2",
    progress: 0,
    elapsed: "—",
    cost: 6,
    tab: "active",
  },
  {
    id: "gen_88aa10",
    state: "complete",
    title: "Hold the Line",
    type: "music",
    model: "Suno v5",
    progress: 100,
    elapsed: "00:04:22",
    cost: 12,
    tab: "complete",
  },
  {
    id: "gen_77bd31",
    state: "complete",
    title: "Open Frame",
    type: "image",
    model: "SDXL",
    progress: 100,
    elapsed: "00:00:42",
    cost: 1,
    tab: "complete",
  },
  {
    id: "gen_12ef55",
    state: "error",
    title: "Drift West — render",
    type: "video",
    model: "Seedance 2",
    progress: 0,
    elapsed: "—",
    cost: 0,
    tab: "failed",
  },
];

export const RENDER_TAB_COUNTS = { active: 3, complete: 124, failed: 2 } as const;

export const CHANNELS: Channel[] = [
  {
    platform: "TikTok",
    handle: "@vocaler_demo",
    subs: "12.4k",
    last: "2m",
    scheduled: 3,
    state: "complete",
  },
  {
    platform: "YouTube",
    handle: "@vocaler_studio",
    subs: "3.2k",
    last: "14m",
    scheduled: 1,
    state: "complete",
  },
  {
    platform: "Instagram",
    handle: "@drift_records",
    subs: "8.1k",
    last: "1h",
    scheduled: 0,
    state: "rendering",
  },
  {
    platform: "SoundCloud",
    handle: "vocaler.demo",
    subs: "—",
    last: "3d",
    scheduled: 2,
    state: "offline",
  },
];

export const GENERATE_CONFIG: GenerateConfig = {
  musicModels: [
    { id: "suno-v5", label: "Suno v5", kind: "music", creditsPerGeneration: 2 },
    { id: "lyria-2", label: "Lyria 2", kind: "music", creditsPerGeneration: 2 },
  ],
  imageModels: [
    { id: "nano-banana-pro", label: "Nano Banana Pro", kind: "image", creditsPerGeneration: 1 },
    { id: "gpt-image", label: "GPT Image", kind: "image", creditsPerGeneration: 1 },
    { id: "sdxl", label: "SDXL", kind: "image", creditsPerGeneration: 1 },
  ],
  videoModels: [
    { id: "seedance-2", label: "Seedance 2", kind: "video", creditsPerGeneration: 6 },
    { id: "visualizer", label: "Visualizer", kind: "video", creditsPerGeneration: 0 },
  ],
  tiers: {
    music: [
      { key: "amateur", label: "Amateur", description: "Fastest, cheapest.", modelId: "musicgen", creditsPerGeneration: 3 },
      { key: "pro", label: "Pro", description: "Polished output you can ship.", modelId: "suno-v4", creditsPerGeneration: 5 },
      { key: "studio", label: "Studio", description: "Higher fidelity.", modelId: "suno-v5", creditsPerGeneration: 8 },
      { key: "legend", label: "Legend", description: "Multi-engine race + composite.", modelId: "best-of", creditsPerGeneration: 18 },
    ],
    image: [
      { key: "amateur", label: "Amateur", description: "Fastest, cheapest.", modelId: "flux-schnell", creditsPerGeneration: 1 },
      { key: "pro", label: "Pro", description: "Polished output you can ship.", modelId: "flux-dev", creditsPerGeneration: 3 },
      { key: "studio", label: "Studio", description: "Higher fidelity.", modelId: "gpt-image-2", creditsPerGeneration: 4 },
      { key: "legend", label: "Legend", description: "Best-in-class.", modelId: "nano-banana-pro", creditsPerGeneration: 5 },
    ],
    video: [
      { key: "pro", label: "Pro", description: "Polished output you can ship.", modelId: "kling", creditsPerGeneration: 15 },
      { key: "legend", label: "Legend", description: "Best-in-class.", modelId: "kling-pro", creditsPerGeneration: 25 },
    ],
  },
  platformFormats: [
    { key: "yt_thumb", label: "YouTube Thumbnail", aspect: "16:9", width: 1280, height: 720, platforms: ["youtube"] },
    { key: "yt_short", label: "YouTube Short Cover", aspect: "9:16", width: 1080, height: 1920, platforms: ["youtube"] },
    { key: "ig_square", label: "Instagram Square", aspect: "1:1", width: 1080, height: 1080, platforms: ["instagram"] },
    { key: "ig_portrait", label: "Instagram Portrait", aspect: "4:5", width: 1080, height: 1350, platforms: ["instagram"] },
    { key: "ig_story", label: "Instagram Story/Reel", aspect: "9:16", width: 1080, height: 1920, platforms: ["instagram"] },
    { key: "tiktok", label: "TikTok Cover", aspect: "9:16", width: 1080, height: 1920, platforms: ["tiktok"] },
    { key: "soundcloud_cover", label: "SoundCloud Artwork", aspect: "1:1", width: 3000, height: 3000, platforms: ["soundcloud"] },
  ],
};

export const WEBHOOKS: WebhookRow[] = [
  {
    url: "https://api.drift.records/hooks/vocaler",
    events: "render.complete, batch.complete",
    status: "active",
    lastTriggered: "2026-05-22 13:04",
  },
];

export const COMMANDS: Command[] = [
  { id: "new-song", label: "New song", group: "Create", icon: "sparkles", hint: "G then S" },
  { id: "upload-json", label: "Upload JSON batch", group: "Create", icon: "upload" },
  { id: "go-home", label: "Go to Home", group: "Navigate", icon: "home" },
  { id: "go-generate", label: "Go to Generate", group: "Navigate", icon: "plus" },
  { id: "go-library", label: "Go to Library", group: "Navigate", icon: "folders" },
  { id: "go-renders", label: "Go to Renders", group: "Navigate", icon: "layers" },
  { id: "go-channels", label: "Go to Channels", group: "Navigate", icon: "broadcast" },
  { id: "go-settings", label: "Go to Settings", group: "Navigate", icon: "settings" },
  { id: "top-up", label: "Top up credits", group: "Account", icon: "bolt" },
  { id: "toggle-theme", label: "Toggle theme", group: "Account", icon: "rectangle" },
];

// ---------- Platform admin (god-mode) mock data ----------
import type {
  AuditEvent,
  PlatformJobRow,
  PlatformTenantDetail,
  PlatformTenantRow,
  PlatformUserDetail,
  PlatformUserRow,
} from "../types";

export const PLATFORM_TENANTS: PlatformTenantRow[] = [
  {
    tenantId: "t_drift",
    name: "Drift Records",
    plan: "pro",
    status: "active",
    memberCount: 4,
    ownerEmails: ["nico@driftrecords.com"],
    monthlyRenderQuota: 500,
    monthlyRendersUsed: 217,
    bonusCredits: 0,
    createdAt: "2026-04-12T09:00:00Z",
    lastActiveAt: "2026-05-31T14:20:00Z",
  },
  {
    tenantId: "t_vocaler_demo",
    name: "Vocaler Demo",
    plan: "studio",
    status: "active",
    memberCount: 2,
    ownerEmails: ["eli@vocaler.studio"],
    monthlyRenderQuota: 5000,
    monthlyRendersUsed: 1842,
    bonusCredits: 200,
    createdAt: "2026-03-01T11:00:00Z",
    lastActiveAt: "2026-05-31T20:00:00Z",
  },
  {
    tenantId: "t_late_channel",
    name: "Late Channel",
    plan: "free",
    status: "delinquent",
    memberCount: 1,
    ownerEmails: ["late@channel.test"],
    monthlyRenderQuota: 10,
    monthlyRendersUsed: 10,
    bonusCredits: 0,
    createdAt: "2026-05-15T19:00:00Z",
    lastActiveAt: "2026-05-29T08:00:00Z",
  },
  {
    tenantId: "t_susp",
    name: "Paused Studio",
    plan: "pro",
    status: "suspended",
    memberCount: 3,
    ownerEmails: ["x@paused.test"],
    monthlyRenderQuota: 500,
    monthlyRendersUsed: 0,
    bonusCredits: 0,
    createdAt: "2026-02-10T11:00:00Z",
    lastActiveAt: "2026-05-01T11:00:00Z",
  },
];

export const PLATFORM_TENANT_DETAILS: Record<string, PlatformTenantDetail> = {
  t_drift: {
    ...PLATFORM_TENANTS[0]!,
    members: [
      { userId: "u_nico", email: "nico@driftrecords.com", role: "owner", status: "active" },
      { userId: "u_mia", email: "mia@driftrecords.com", role: "admin", status: "active" },
    ],
    recentJobs: [],
    recentEvents: [
      { ts: "2026-05-31T14:20:00Z", kind: "job", message: "music completed" },
    ],
    integrations: [
      { providerId: "suno", configured: true, lastTestOk: true },
    ],
    billing: {
      stripeCustomerId: "cus_xxx",
      subscriptionStatus: "active",
      currentPeriodEnd: "2026-06-12T00:00:00Z",
      paymentFailureCount: 0,
    },
  },
};

export const PLATFORM_USERS: PlatformUserRow[] = [
  {
    userId: "u_owner_op",
    email: "owner@vocaler.ai",
    isOperator: true,
    status: "active",
    tenantCount: 1,
    createdAt: "2026-02-01T09:00:00Z",
    lastSeenAt: "2026-05-31T20:00:00Z",
  },
  {
    userId: "u_nico",
    email: "nico@driftrecords.com",
    isOperator: false,
    status: "active",
    tenantCount: 1,
    createdAt: "2026-04-12T09:00:00Z",
    lastSeenAt: "2026-05-31T14:20:00Z",
  },
  {
    userId: "u_eli",
    email: "eli@vocaler.studio",
    isOperator: false,
    status: "active",
    tenantCount: 1,
    createdAt: "2026-03-01T11:00:00Z",
    lastSeenAt: "2026-05-31T20:00:00Z",
  },
];

export const PLATFORM_USER_DETAILS: Record<string, PlatformUserDetail> = {
  u_nico: {
    ...PLATFORM_USERS[1]!,
    memberships: [
      {
        tenantId: "t_drift",
        tenantName: "Drift Records",
        role: "owner",
        status: "active",
        invitedAt: null,
        joinedAt: "2026-04-12T09:00:00Z",
      },
    ],
    recentActivity: [],
  },
};

export const PLATFORM_JOBS: PlatformJobRow[] = [
  {
    jobId: "job_0abc",
    tenantId: "t_drift",
    tenantName: "Drift Records",
    kind: "music",
    model: "suno-v5",
    status: "completed",
    progress: 100,
    creditsCharged: 8,
    ownerEmail: "nico@driftrecords.com",
    queuedAt: "2026-05-31T13:48:00Z",
    finishedAt: "2026-05-31T13:51:00Z",
    errorShort: null,
  },
  {
    jobId: "job_0xyz",
    tenantId: "t_late_channel",
    tenantName: "Late Channel",
    kind: "video",
    model: "kling",
    status: "failed",
    progress: 10,
    creditsCharged: 0,
    ownerEmail: "late@channel.test",
    queuedAt: "2026-05-31T11:00:00Z",
    finishedAt: "2026-05-31T11:00:30Z",
    errorShort: "Replicate video requires an API token.",
  },
];

export const PLATFORM_AUDIT: AuditEvent[] = [
  {
    id: "ev_abc",
    ts: "2026-05-31T14:30:00Z",
    actorEmail: "owner@vocaler.ai",
    actorIsOperator: true,
    target: { kind: "tenant", id: "t_susp", label: "Paused Studio" },
    action: "tenant.suspend",
    detail: "Suspended via studio platform admin",
  },
  {
    id: "ev_def",
    ts: "2026-05-31T13:45:00Z",
    actorEmail: "nico@driftrecords.com",
    actorIsOperator: false,
    target: { kind: "integration", id: "suno", label: "Suno" },
    action: "integration.save",
    detail: null,
  },
];
